import { cx } from '@linaria/core';
import { InputHTMLAttributes, ReactNode, Ref, RefObject, TextareaHTMLAttributes, useEffect, useRef, useState } from 'react';
import { getScreenState } from '../../state/getState';
import { updatePortalState } from '../../state/internal/layout';
import { AppRouter } from '../../state/state.router';
import { isAndroid, isIpad } from '../../utilities/browser';
import { useScreenStateSelector } from '../Hooks/useScreenState';
import "./Input.linaria.module.css!=!./Input?./Input.linaria.module.css?css=H4sIAAAAAAAAA3VUy46jMBC871fMZaWdg0eYQB7sx1gG2mDwK7Z5Ddp/X5EEgpHmEqlS1XRDV/UX9ZLkPcHtcFEymZlWPuOqBsv933+/vhaa2Y0W4D1Y5AwtuKqOwrYklZgGJmfdecEVZEorWNn4vLI5LdrK6k6VoaAkuImvzRgf4K4AFVpom3lLlTPUgtqaXwTBnN2khPmpKTq78A+wiqgkbX5R/ffMBIwZXv9vcsJLw6pkllyhgZe+zqKVPLGVzLUtwb6Zim9l1FZcvRmhCZ6SRAt2gLOhZclVhbw2WWLGrQAITqb4MnUHuBXk2nsts3RX077ah2irEMD8biQRqMVRbXlV7+QQE2xu+J6wuYYHhb+S0yW1IFfJzRHcCRhZPpfcGUGnLBe6aLeVlASrkY91PxfUgn8tr6f2D0KVBVBIsuJzldt4WzgaIG+5R54aVPOqFssAP+9e4WqKU2LkPaVZZgQtoNaiBDvvO5ZcokIrb6nziBdaua21bInzarheZ6Md91yrjOZOi85v7mQngu9SiryZuXLgM4TN+IFOyw9+70Q2q8uN5moJC/SgvAucnjZbop6WQpaWvHPHQJ0Ywcnl1r+tt/R0WvDy4/VOwGgnPOLKdB49Rdtbpd8YS0MwnM/tJc1q3YNdHxQfHkQLz3v43PlXX6iufIgC9+I0sO9eD0f9y7z7kuUjvDIRfoR9KnTgWX307DJGtJthL4aj+DVDtItPKdrr2IcojA/eTyOCAnEseCZoX7FuoGnSmspwA8dVHjbQTwRrd9dxPz/vEY6i3ytZXMmAq37KQ/Q438jxb8jw6T1F4UjOVKVZiJ7q4RnuNIr2scbnKaLtFmuulnOOlqO5qmqC67Nuuu8QzVTwSiHuQbqsgCUBu5DVsZTN+R0yC4Iub70d1NN6dA5wfvT/6RAtt2roKqfS9VbRzuut77RG0sLj27yi+B9xMiCA/AYAAA=="
export const INPUT_LINE_HEIGHT = '1.4375rem';
const inputStyles = "atm_bv_1kw7nm4 atm_fr_1kw7nm4 atm_kd_glywfm atm_26_glywfm atm_2d_1j28jx2 atm_7l_1if9mme atm_am_kb7nvz atm_jb_idpfg4 atm_3f_idpfg4 atm_gi_idpfg4 atm_lo_1y44olf atm_le_14y27yu atm_lk_idpfg4 atm_ll_idpfg4 atm_e2_1p91q4f atm_9s_1ulexfb atm_7d_1nxixhv atm_r2_1j28jx2 atm_n1gy25_pmq5a";
const outlinedBorderStyles = "atm_mk_stnw88 atm_f3_1qmmlbj atm_mj_glywfm atm_5j_1kw7nm4 atm_3f_1479vg4 atm_5z11mp_1e66k75";
// const focusedOutlinedBorderStyles = css`
//     border: 2px solid ${palette.greenMFC};
//     &:hover {
//         border: 2px solid ${palette.greenMFC};
//     }
// `

const outlinedStyles = "atm_lo_o7aogt atm_le_o7aogt atm_lk_idpfg4 atm_ll_idpfg4";
const outlinedContainerStyles = "atm_3f_1479vg4 atm_5j_1y44olf atm_lo_idpfg4 atm_le_idpfg4 atm_lk_dlk8xv atm_ll_dlk8xv atm_5z11mp_1jj5ham";
const fullWidthContainerStyles = "atm_vy_1osqo2v";
const inputContainerStyles = "atm_c8_w1gvyb atm_cs_bfngof atm_9s_116y0ak atm_am_kb7nvz atm_h_1h6ojuz atm_mk_h2mmj6 atm_g3_1p91q4f";
const multilineStyles = "atm_e2_1wugsn5 atm_my_glywfm";
const shouldPinVideo = () => {
  const isMobile = window.isIOS || isIpad || isAndroid;
  const {
    windowHeight
  } = getScreenState();
  const current = window?.visualViewport?.height || window?.innerHeight;
  const isKeyboardOpen = windowHeight && current ? windowHeight - current > 1e-7 : false;
  const zoom = window?.visualViewport?.scale && window?.visualViewport.scale - 1 > 1e-7;
  return isKeyboardOpen && !zoom && isMobile;
};
interface FieldChromeProps {
  /** The wrapper holding the field and its adornments */
  containerRef?: Ref<HTMLDivElement>;
  outlined?: boolean;
  fullWidth?: boolean;
  quietFocus?: boolean;
  noTrackFocus?: boolean;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
}
export interface InputProps extends FieldChromeProps, InputHTMLAttributes<HTMLInputElement> {
  multiline?: false;
  ref?: any;
}

/** A `<textarea rows={1}>` in place of the `<input type="search">` */
export interface MultilineInputProps extends FieldChromeProps, TextareaHTMLAttributes<HTMLTextAreaElement> {
  multiline: true;
  ref?: RefObject<HTMLTextAreaElement | null>;
}
const Input = (props: InputProps | MultilineInputProps) => {
  const {
    name,
    ref,
    containerRef,
    className,
    outlined = false,
    fullWidth = false,
    quietFocus = false,
    noTrackFocus = false,
    endAdornment,
    startAdornment,
    onFocus,
    onBlur,
    ...fieldProps
  } = props;
  const [focused, setFocused] = useState(false);
  const portrait = useScreenStateSelector('portrait');

  // mirrors `focused` for the unmount cleanup below (state is stale there)
  const focusedRef = useRef(false);
  // the delayed iPad focus write below — must die on blur/unmount or it
  // re-sets composeFocused=true after the release that followed it
  const focusTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const onInputFocus = event => {
    focusedRef.current = true;
    if (!noTrackFocus) {
      // if we're mobile and the keyboard is open, then we shouldn't quiet focus,
      // and if we are on desktop, then we should basically always quiet focus
      if (isIpad) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = setTimeout(() => {
          const shouldPinToTop = shouldPinVideo();
          updatePortalState({
            composeFocused: true,
            quietFocus: quietFocus || !shouldPinToTop
          }, false);
        }, 100);
      } else {
        const shouldPinToTop = shouldPinVideo();
        updatePortalState({
          composeFocused: true,
          quietFocus: quietFocus || !shouldPinToTop
        }, false);
      }
    }
    setFocused(true);
    onFocus?.(event);
  };
  const onInputBlur = event => {
    focusedRef.current = false;
    clearTimeout(focusTimerRef.current);
    if (!noTrackFocus) {
      updatePortalState({
        composeFocused: false,
        quietFocus: quietFocus
      }, false);
    }
    setFocused(false);
    onBlur?.(event);
  };
  useEffect(() => {
    if (window.isIOS || isIpad || isAndroid) {
      // orientation has changed
      ref?.current?.blur();
      updatePortalState({
        composeFocused: false,
        quietFocus: quietFocus
      });
    }
  }, [portrait, quietFocus]);
  // an input unmounted while focused never fires blur, so the global
  // composeFocused it set would stay true forever (hiding the viewer
  // count, muting global key handlers) — release it on the way out, and
  // re-claim it when StrictMode replays this effect on a still-focused input
  useEffect(() => {
    if (!noTrackFocus && focusedRef.current) {
      updatePortalState({
        composeFocused: true
      }, false);
    }
    return () => {
      clearTimeout(focusTimerRef.current);
      if (!noTrackFocus && focusedRef.current) {
        updatePortalState({
          composeFocused: false
        }, false);
      }
    };
  }, []);
  useEffect(() => {
    const onResize = () => {
      // this will run when keyboard opens to correct it if the keyboard opens
      const shouldPinToTop = shouldPinVideo();
      if (!noTrackFocus) {
        updatePortalState({
          quietFocus: quietFocus || !shouldPinToTop
        }, false);
      }
      if (!shouldPinToTop && isIpad) {
        // if the keyboard got closed (keyboard was dismissed) we should blur the input so user
        // can properly interact with the rest of the room (like dragging down video) without
        // keyboard popping open again
        ref?.current?.blur();
      }
      // only scroll if we're on iPad, this particular input is focused, it's not a quietFocus input, and we're in room
      if (isIpad && !quietFocus && focused && AppRouter.route.visiblePage === 'room') {
        // force scroll incase ipad safari didn't push the viewport as it should
        window.scrollBy(0, 2000);
      }
    };
    window.visualViewport?.addEventListener?.('resize', onResize, {
      passive: true
    });
    return () => {
      window.visualViewport?.removeEventListener?.('resize', onResize);
    };
  }, [quietFocus, focused, noTrackFocus]);
  const fieldClassName = cx('mfc-input', focused && 'mfc-focused', inputStyles, outlined && outlinedStyles);
  let field: ReactNode;
  if (fieldProps.multiline === true) {
    const {
      multiline,
      ...textareaProps
    } = fieldProps;
    field = <textarea name={name} ref={ref} onFocus={onInputFocus} onBlur={onInputBlur} autoComplete="off" rows={1} {...textareaProps} className={cx(fieldClassName, multilineStyles)} />;
  } else {
    const {
      multiline,
      ...inputProps
    } = fieldProps;
    field = <input name={name} ref={ref} onFocus={onInputFocus} onBlur={onInputBlur} autoComplete="off" type="search" {...inputProps} className={fieldClassName} />;
  }
  return <div ref={containerRef} className={cx(inputContainerStyles, fullWidth && fullWidthContainerStyles, outlined && outlinedContainerStyles, className)}>
            {startAdornment}
            {field}
            {endAdornment}
            {outlined && <fieldset className={cx(outlinedBorderStyles
    // focused && focusedOutlinedBorderStyles,
    )} />}
        </div>;
};
export default Input;